// src/components/approval/MobileApprovalLine.jsx
import React, { useMemo } from "react";

/**
 * 모바일 최적화 결재선 컴포넌트
 * - 하나의 박스 안에 결재자들을 그리드로 표시
 * - 스크린샷 디자인 참고
 */
export default function MobileApprovalLine({ lines: linesProp = [] }) {
    // ✅ Hook은 early return 전에 호출되어야 함
    // 다양한 키 이름에 대비해 안전하게 필드 정규화 (웹 버전과 동일)
    const lines = useMemo(() => {
        if (!Array.isArray(linesProp)) return [];
        return linesProp.map((l, idx) => ({
            id: l.id ?? idx,
            order: Number(l.order ?? idx + 1),
            approverName:
                l.approver_name ?? l.username ?? l.approver ?? l.name ?? "결재자 미상",
            approverPosition: l.approver_position ?? l.position ?? "",
            category:
                l.approval_category ??
                l.category ??
                (l.status === "reference" ? "reference" : "approval"),
            // is_approved: submit | approved | rejected | wait 등
            state:
                l.is_approved ??
                l.state ??
                (l.status && ["approved", "rejected"].includes(l.status)
                    ? l.status
                    : "wait"),
            approvedAt: l.approved_at ?? l.approvedAt ?? null,
            signFile: l.sign_file ?? l.signFile ?? null, // ✅ 서명 파일 정규화
        }));
    }, [linesProp]);

    // 직급 우선순위 (낮을수록 먼저 표시 = 직급 낮은 순)
    const positionRank = {
        '사원': 1, '주임': 2, '대리': 3, '과장': 4, '차장': 5,
        '부장': 6, '이사': 7, '상무': 8, '전무': 9, '부사장': 10, '사장': 11
    };

    const getPositionRank = (position) => {
        if (!position) return 999;
        for (const [key, rank] of Object.entries(positionRank)) {
            if (position.includes(key)) return rank;
        }
        return 999;
    };

    // order 0 제외, 직급 순 정렬 (직급 같으면 이름순)
    const approvals = lines
        .filter((l) => l.order !== 0 && l.category !== "reference")
        .sort((a, b) => {
            const rankA = getPositionRank(a.approverPosition);
            const rankB = getPositionRank(b.approverPosition);
            if (rankA === rankB) {
                return (a.approverName || '').localeCompare(b.approverName || '', 'ko');
            }
            return rankA - rankB;
        });

    // ✅ 정규화된 데이터로 빈 배열 체크
    if (approvals.length === 0) return null;

    // 열 개수 계산 (최대 4열, 최소 3열)
    const cols = Math.min(4, Math.max(3, approvals.length));

    return (
        <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden mb-4">
            {/* 헤더 */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700">결재선</h3>
                <span className="text-xs text-slate-400">{approvals.length}명</span>
            </div>

            {/* 결재자 그리드 */}
            <div className={`grid gap-2 p-3`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {approvals.map((line, idx) => {
                    // ✅ 정규화된 필드 사용
                    const state = line.state;
                    const sign = line.signFile;

                    // Styles by status
                    const isApproved = state === "approved";
                    const isRejected = state === "rejected";

                    return (
                        <div
                            key={line.id ?? idx}
                            className="flex flex-col items-center p-2 rounded-xl border border-slate-100 bg-white"
                        >
                            {/* 순서 뱃지 */}
                            <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center mb-1.5">
                                {idx + 1}
                            </div>

                            {/* 이름 */}
                            <p className="text-xs font-semibold text-slate-800 text-center truncate w-full">
                                {line.approverName || "미상"}
                            </p>

                            {/* 직책 */}
                            <p className="text-[10px] text-blue-500 text-center truncate w-full mb-2">
                                {line.approverPosition || "-"}
                            </p>

                            {/* 서명/상태 영역 */}
                            <div className="w-full h-10 flex items-center justify-center border border-dashed border-slate-200 rounded-lg bg-slate-50">
                                {isApproved && sign ? (
                                    <img
                                        src={sign}
                                        alt="서명"
                                        className="max-h-8 max-w-full object-contain"
                                        onError={(e) => {
                                            // ✅ 이미지 로드 실패 시 대체 텍스트 표시
                                            e.target.style.display = 'none';
                                            e.target.nextElementSibling.style.display = 'block';
                                        }}
                                    />
                                ) : null}
                                {isApproved && !sign && (
                                    <span className="text-[10px] font-medium text-green-600">승인</span>
                                )}
                                {isRejected && (
                                    <span className="text-[10px] font-medium text-red-600">반려</span>
                                )}
                                {!isApproved && !isRejected && (
                                    <span className="text-[10px] font-medium text-slate-400">미결</span>
                                )}
                                {/* 이미지 로드 실패 시 대체 텍스트 */}
                                <span className="text-[10px] font-medium text-green-600" style={{ display: 'none' }}>승인</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
